import _ from 'lodash';
import {
  determineErrorFromResponse,
  determineErrorIfStaticRender,
  urlIsExternal,
  NAME_RECOMMENDED_SERIES,
} from 'helpers/ContentHelper';
import {
  CLEAR_SERIES_DETAIL,
  FETCH_DETAIL_CATEGORY_ANONYMOUS,
  FETCH_DETAIL_CATEGORY,
  FETCH_EPISODE_DETAIL,
  FETCH_SERIES_DETAIL,
  FETCH_SERIES_URL,
  FETCH_V2_SEO,
  SET_SERIES_DETAIL_ERROR,
  SET_SERIES_DETAIL_TABS,
} from './SeriesDetailActions';
import { getDetailData, isInlineComponent, isSeasonPanel } from './SeriesDetailHelpers';

const INITIAL_STATE = {
  continueWatchingItems: [],
  episodes: [],
  errors: null,
  inline: null,
  isLatestEpisodeLive: null,
  latestEpisode: null,
  pagesSeo: {},
  links: null,
  recommendedPanel: {},
  seo: null,
  seriesData: null,
  seriesScreenUrl: null,
  seriesSortOrder: null,
  seriesType: null,
  tabs: null,
};

export default function(state = INITIAL_STATE, action) {
  let errors = determineErrorFromResponse(action);  
  switch (action.type) {
    case FETCH_SERIES_URL: {
      const data = _.get(action, 'payload.data');
      const seriesScreenUrl = _.get(data, 'screenUrl', null);
      if (!seriesScreenUrl && !errors) {
        errors = determineErrorIfStaticRender();
      }
      return {
        ...state,
        errors,
        seriesScreenUrl,
      };
    }
    case FETCH_DETAIL_CATEGORY: {
      const data = _.get(action, 'payload.data');
      const panels = _.get(data, 'panels.member', null);
      if (errors) return { ...state, errors };
      if (!data || !panels) return state;
      const seriesData = data;
      const inlineComponent = data;
      const { description, name, images = {}, latestVideo: latestEpisode } = data;
      const {
        calendar,
        continueWatching,
        favoritableItems,
        isLatestEpisodeLive,
        oldestEpisode,
        promos,
        seo,
        seriesType,
        tabComponents,
      } = getDetailData({ data, latestEpisode, inlineComponent, seriesData, panels });

      if (name && description) {
        const bioComponent = {
          '@type': 'Biography',
          headline: 'Biography',
          description,
          name,
          images,
          id: 'biography',
        };

        tabComponents.push(bioComponent);
      }

      return {
        ...state,
        calendar,
        continueWatching,
        errors,
        favoritableItems,
        inline: inlineComponent,
        latestEpisode,
        isLatestEpisodeLive,
        oldestEpisode,
        promos,
        seo,
        seriesData,
        seriesType,
        tabs: tabComponents,
      };
    }
    case FETCH_DETAIL_CATEGORY_ANONYMOUS: {
      const data = _.get(action, 'payload.data');
      const panels = _.get(data, 'panels.member', null);
      if (errors) return { ...state, errors };
      if (!data || !panels) return state;
      const seriesData = data;
      const inlineComponent = data;
      const { description, name, images = {}, latestVideo: latestEpisode, id = null } = data;
      const {
        calendar,
        continueWatching,
        favoritableItems,
        isLatestEpisodeLive,
        oldestEpisode,
        promos,
        seo,
        seriesType,
        tabComponents,
      } = getDetailData({ data, latestEpisode, inlineComponent, seriesData, id, panels });

      if (name && description) {
        const bioComponent = {
          '@type': 'Biography',
          headline: 'Biography',
          description,
          name,
          images,
          id: 'biography',
        };
        tabComponents.push(bioComponent);
      }
      const latestEntry = _.get(tabComponents, '0');
      return {
        ...state,
        calendar,
        continueWatching,
        errors,
        favoritableItems,
        inline: inlineComponent,
        latestEpisode,
        isLatestEpisodeLive,
        oldestEpisode,
        promos,
        seo,
        seriesData,
        seriesType,
        tabs: latestEntry ? [latestEntry] : [],
      };
    }
    case FETCH_SERIES_DETAIL: {
      const data = _.get(action, 'payload.data');
      const panels = _.get(data, 'panels.member', null);
      if (!data || !panels)
        return {
          ...state,
          errors,
        };

      const recommendedPanel = panels.filter(
        panel => panel.headline === NAME_RECOMMENDED_SERIES,
      )?.[0];

      const screenTypeId = _.get(data, 'id', null);
      const seriesData = _.get(panels, '0.items.member.0');
      const seasonPanelId = 1;
      const seasonPanels = _.get(panels, `${seasonPanelId}.items.member`);
      if (seasonPanels) {
        seasonPanels.forEach((season, index) => {
          if (season.url && urlIsExternal(season.url) && isSeasonPanel(season)) {
            const seasonUrlArray = season.url.split('/');
            const seasonUrlPath = _.last(seasonUrlArray);
            panels[seasonPanelId].items.member[index].url = seasonUrlPath;
          }
        });
      }

      const inlineComponent = _.get(
        panels.filter(elem => isInlineComponent(elem)),
        '0',
      );
      const latestEpisode = _.get(inlineComponent, 'items.member.0.latestEpisode');
      const {
        calendar,
        continueWatching,
        continueWatchingItems,
        favoritableItems,
        links,
        isLatestEpisodeLive,
        oldestEpisode,
        promos,
        seo,
        seriesType,
        seriesSortOrder,
        tabComponents,
      } = getDetailData({ data, latestEpisode, inlineComponent, seriesData, screenTypeId, panels });
      return {
        ...state,
        calendar,
        continueWatching,
        continueWatchingItems,
        errors,
        favoritableItems,
        inline: inlineComponent,
        latestEpisode,
        links,
        isLatestEpisodeLive,
        oldestEpisode,
        promos,
        recommendedPanel,
        seo,
        seriesData,
        seriesType,
        seriesSortOrder,
        tabs: tabComponents,
      };
    }
    case FETCH_EPISODE_DETAIL: {
      const data = _.get(action, 'payload.data');
      const { episodes: currentEpisodes } = state;
      const currentSeason = data?.member ?? [];
      const episodes = [...currentSeason, ...currentEpisodes];
      return {
        ...state,
        episodes,
      };
    }
    case FETCH_V2_SEO: {
      const data = _.get(action, 'payload.data');
      return {
        ...state,
        pagesSeo: data ?? {},
      };
    }
    case SET_SERIES_DETAIL_ERROR: {
      return {
        ...state,
        errors,
      };
    }
    case CLEAR_SERIES_DETAIL: {
      return {
        ...state,
        ...INITIAL_STATE,
      };
    }
    case SET_SERIES_DETAIL_TABS: {
      return {
        ...state,
        tabs: action.payload,
      };
    }
    default:
      return state;
  }
}
